import { supabase, auth, isSupabaseConfigured } from '../lib/supabase';

/**
 * Interface pour les réponses d'Edge Functions
 */
interface EdgeFunctionResponse<T = any> {
  success?: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Appel sécurisé d'une Edge Function avec gestion d'erreurs complète
 */
export async function callEdgeFunction<T = any>(
  functionName: string, 
  payload?: any,
  options: {
    timeout?: number;
    retries?: number;
  } = {}
): Promise<T> {
  const { timeout = 30000, retries = 1 } = options;

  // Validation de la configuration
  if (!isSupabaseConfigured()) {
    throw new Error(`Configuration Supabase manquante. 
    
📋 Pour corriger :
1. Cliquez sur "Connect to Supabase" en haut à droite
2. Ou configurez manuellement les variables :
   - VITE_SUPABASE_URL
   - VITE_SUPABASE_ANON_KEY`);
  }

  // Validation des paramètres
  if (!functionName || typeof functionName !== 'string') {
    throw new Error('Nom de fonction requis et doit être une chaîne de caractères.');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  // Récupération de la session
  let session;
  try {
    const { session: currentSession, error: sessionError } = await auth.getSession();
    if (sessionError) throw sessionError;
    session = currentSession;
  } catch (error) {
    console.error('[EDGE] Erreur récupération session:', error);
    throw new Error('Session invalide. Veuillez vous reconnecter.');
  }

  if (!session?.access_token) {
    throw new Error('Non authentifié. Veuillez vous connecter pour accéder à cette fonctionnalité.');
  }

  const functionUrl = `${supabaseUrl}/functions/v1/${functionName}`;
  
  console.log(`[EDGE] Appel ${functionName}:`, {
    url: functionUrl,
    hasToken: !!session.access_token,
    payload: payload ? Object.keys(payload) : 'aucun'
  });

  // Fonction d'appel avec retry
  const makeRequest = async (attempt: number = 1): Promise<T> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${session.access_token}`, 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'apikey': supabaseAnonKey,
          'X-Client-Info': 'cress-guadeloupe-platform'
        },
        body: payload ? JSON.stringify(payload) : undefined,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Gestion des erreurs HTTP
      if (!response.ok) {
        let errorMessage = `Erreur ${response.status}: ${response.statusText}`;
        let errorDetails = '';
        
        try {
          const errorText = await response.text();
          errorDetails = errorText;
          
          // Tentative de parsing JSON pour plus de détails
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.error || errorJson.message || errorMessage;
          } catch {
            // Si ce n'est pas du JSON, utiliser le texte brut
            if (errorText) {
              errorMessage = errorText;
            }
          }
        } catch {
          // Impossible de lire le body de la réponse
        }
        
        console.error(`[EDGE] Erreur ${functionName} (tentative ${attempt}):`, {
          status: response.status,
          statusText: response.statusText,
          errorMessage,
          errorDetails
        });
        
        // Messages d'erreur spécifiques
        if (response.status === 401) {
          throw new Error('Session expirée. Veuillez vous reconnecter.');
        }
        
        if (response.status === 403) {
          if (errorMessage.toLowerCase().includes('admin') || 
              errorMessage.toLowerCase().includes('forbidden')) {
            throw new Error('Accès refusé : rôle administrateur requis pour cette action.');
          }
          throw new Error('Accès refusé. Vous n\'avez pas les permissions nécessaires.');
        }
        
        if (response.status === 404) {
          throw new Error(`Fonction '${functionName}' non trouvée. Vérifiez qu'elle est déployée dans Supabase.`);
        }
        
        if (response.status >= 500) {
          throw new Error(`Erreur serveur (${response.status}). Veuillez réessayer plus tard.`);
        }
        
        throw new Error(errorMessage);
      }
      
      // Parsing de la réponse
      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        console.warn('[EDGE] Réponse non-JSON reçue:', parseError);
        result = {};
      }
      
      console.log(`[EDGE] Succès ${functionName}:`, result);
      return result;
      
    } catch (error) {
      clearTimeout(timeoutId);
      
      // Gestion des timeouts
      if (error instanceof Error && error.name === 'AbortError') {
        const timeoutError = new Error(`Timeout: La fonction '${functionName}' n'a pas répondu dans les ${timeout/1000}s.
        
🔍 Causes possibles :
- Fonction non déployée dans Supabase
- Problème de réseau
- Fonction en cours d'initialisation (cold start)
        
💡 Solutions :
- Vérifiez que la fonction est déployée
- Réessayez dans quelques secondes
- Contactez l'administrateur si le problème persiste`);
        
        if (attempt < retries) {
          console.log(`[EDGE] Retry ${functionName} (${attempt + 1}/${retries})...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          return makeRequest(attempt + 1);
        }
        
        throw timeoutError;
      }
      
      // Gestion des erreurs réseau
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        const networkError = new Error(`Erreur réseau: Impossible d'atteindre la fonction '${functionName}'.
        
🔍 Causes possibles :
- Pas de connexion internet
- Fonction '${functionName}' non déployée
- URL Supabase incorrecte: ${supabaseUrl}
- Edge Functions désactivées dans votre projet
        
💡 Solutions :
- Vérifiez votre connexion internet
- Déployez la fonction : npx supabase functions deploy ${functionName}
- Vérifiez l'URL Supabase dans vos variables d'environnement`);
        
        if (attempt < retries) {
          console.log(`[EDGE] Retry réseau ${functionName} (${attempt + 1}/${retries})...`);
          await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
          return makeRequest(attempt + 1);
        }
        
        throw networkError;
      }
      
      // Re-throw autres erreurs
      throw error;
    }
  };

  return makeRequest();
}

/**
 * Créer un utilisateur avec rôle (nécessite droits admin)
 */
export async function createUserWithRole(
  email: string,
  password: string,
  options: {
    role?: string;
    metadata?: Record<string, any>;
    profile_data?: {
      profile_type?: string;
      full_name?: string;
      phone?: string;
      organization?: string;
      address?: string;
      city?: string;
      postal_code?: string;
    };
  } = {}
): Promise<{ userId?: string; error?: string }> {
  try {
    const result = await callEdgeFunction('create-user', {
      email: email.trim().toLowerCase(),
      password,
      email_confirm: true,
      metadata: options.metadata || {},
      set_role: options.role,
      profile_data: options.profile_data
    });
    
    return { userId: result.user?.id, error: null };
  } catch (error) {
    console.error('[EDGE] Erreur création utilisateur:', error);
    return { error: error instanceof Error ? error.message : 'Erreur inconnue' };
  }
}

/**
 * Définir le rôle d'un utilisateur (nécessite droits admin)
 */
export async function setUserRole(
  targetEmail: string,
  role: string,
  mode: 'set' | 'add' | 'remove' = 'set'
): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await callEdgeFunction('set-user-role', {
      target_email: targetEmail.trim().toLowerCase(),
      role: role.toLowerCase(),
      mode
    });

    return { success: true };
  } catch (error) {
    console.error('[EDGE] Erreur modification rôle:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erreur inconnue'
    };
  }
}

/**
 * Envoyer un email d'approbation avec lien de réinitialisation de mot de passe
 */
export async function sendApprovalEmail(data: {
  email: string;
  full_name: string;
  role: string;
  organization?: string;
  territory_name?: string;
}): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await callEdgeFunction('send-approval-email', {
      email: data.email.trim().toLowerCase(),
      full_name: data.full_name,
      role: data.role.toUpperCase(),
      organization: data.organization,
      territory_name: data.territory_name
    });

    return { success: true };
  } catch (error) {
    console.error('[EDGE] Erreur envoi email approbation:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erreur inconnue'
    };
  }
}

/**
 * Wrapper legacy pour compatibilité
 */
export async function callEdgeWithSessionJWT<T = any>(
  functionName: string, 
  payload: any
): Promise<T> {
  return callEdgeFunction<T>(functionName, payload);
}

// Export des fonctions pour compatibilité
export { callEdgeFunction as callEdge };